import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '../../../../components';
import { FtuxTarget } from '../../../../features/ftux/FtuxTarget';
import { DEFAULT_MAILBOX_QUOTA_BYTES } from '../../../../mock-data/mailbox-records';
import { formatMailboxQuota, type MailboxPriceView } from '../../state';
import { FreeTile, PaidTile, Copy, Title, Domain, Price, PriceSlot, Body, Actions } from './AddTiles.styles';

export type AddTilesProps = {
  /** The brand's mail domain, named in the free tile's title. */
  domain: string;
  /** Whether the next mailbox takes the free slot: no `free` mailbox exists yet (the section's one
   *  next-slot rule: the list on the live path, the entitlement on the mock). */
  freeAvailable: boolean;
  /** What a paid mailbox costs, as the service answered (see `mailboxPriceView`): the paid card's
   *  price line. No number until it is `priced`. */
  price: MailboxPriceView;
  /** The free slot is spoken for: its create is still in flight (perhaps from a visit the operator
   *  has left) or its success has not been shown yet. The free card stays, disabled, with its own
   *  label, rather than vanishing and coming back; offering it again would make a second create paid. */
  freeBusy?: boolean;
  /** Create free mailbox: opens the create form on the free path. */
  onCreateFree?: () => void;
  /** Create mailbox (paid): opens the create form on the paid path. */
  onCreatePaid?: () => void;
};

/**
 * The add card at the head of the grid (Figma OWS-Flow 46:877). ALWAYS FIRST, at every count, so the
 * create action never moves. The trade-off: at eight mailboxes the operator scrolls past it to reach
 * their own inbox; if a typical brand grows past a handful, revisit this.
 *
 * ONE card at a time, saying what the next mailbox is: the free card while no `free` mailbox exists,
 * the paid card after. Never both: a second, locked card beside the first advertised a mailbox the
 * operator could not make yet and cost the clarity of the sequence. The price is in the create form's
 * banner too, so nothing is lost.
 *
 * The price is the service's (`price`), charged once: the paid card names that figure and no
 * period. Before it has arrived, or when the read failed, the line is a dash; when charging is
 * switched off (`0`), it says the mailbox is free of charge.
 */
export const AddTiles = ({
  domain,
  freeAvailable,
  price,
  freeBusy = false,
  onCreateFree,
  onCreatePaid,
}: AddTilesProps): React.ReactElement => {
  const { t } = useTranslation();
  const quota = formatMailboxQuota(DEFAULT_MAILBOX_QUOTA_BYTES);

  return (
    <>
      {freeAvailable ? (
        <FreeTile data-testid="mailbox-add-free" data-busy={freeBusy}>
          <Copy>
            {/* One i18n key for the whole sentence (word order moves in Spanish), then split around
                the domain so only that span takes the accent. `Text` cannot style a substring of
                its interpolation, which is the one reason this is a t() call rather than a `text`
                prop. */}
            <Title>
              {t('Add free mail on {{domain}}', { domain })
                .split(domain)
                .map((part, i, parts) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < parts.length - 1 ? <Domain>{domain}</Domain> : null}
                  </React.Fragment>
                ))}
            </Title>
            <Body
              text="One mailbox is included with your brand, free forever. Real storage, IMAP and SMTP. Add it to any mail client."
              tag="span"
            />
          </Copy>
          <Actions>
            {/* Where FTUX - Getting Started lights the free card's button (inert outside an FTUX step). The shared
                Button forwards no attribute, so a span hugging it carries the mark, on the busy one too. */}
            <FtuxTarget id="mail-create-free">
              {freeBusy ? (
                <Button label={t('Finishing setup…')} variant="primary" disabled />
              ) : (
                <Button label={t('Create free mailbox')} variant="primary" onClick={onCreateFree} />
              )}
            </FtuxTarget>
          </Actions>
        </FreeTile>
      ) : (
        <PaidTile data-testid="mailbox-add-paid">
          <Copy>
            <Title $muted>
              <Text text="More mailboxes" tag="span" />
            </Title>
            {/* The service's price, or a dash while there is none to show: never a stale or made-up
                figure. Short on purpose: a 20px line that wraps would make this card taller than the
                tiles beside it. */}
            <PriceSlot data-testid="mailbox-add-paid-price">
              {price.kind === 'priced' ? (
                <Price text="{{price}} each" placeholders={{ price: price.label }} tag="span" />
              ) : price.kind === 'free' ? (
                <Price text="Free of charge" tag="span" />
              ) : (
                <Price text="–" tag="span" />
              )}
            </PriceSlot>
            <Body text="{{quota}} each, same domain, same setup." placeholders={{ quota }} tag="span" />
          </Copy>
          <Actions>
            <Button label={t('Create mailbox')} variant="primary" onClick={onCreatePaid} />
          </Actions>
        </PaidTile>
      )}
    </>
  );
};

export default AddTiles;
